<!--
  Ordini da firmare — orders awaiting carrier signature.
  Redesigned 2026-06-12 (v2.7.0 path, Phase 1.5 Chunk D.1).

  Drops kendo-grid × 2 + kendo-datepicker × 2 + kendo-dropdownlist +
  mat-spinner + ~50 inline `style="..."` attributes (57 kendo refs
  total → 0).

  Two-step UX (single-table swap):
    • List mode (chosenCarrier === null):
      sticky header + filter bar + table 8 cols (N° Ordine /
      Operatore / Corriere / Cliente / N° Colli / Data ordine /
      Data evasione / Completa / Azioni). Click row → detail modal.
    • Sign-batch mode (chosenCarrier set):
      header switches to "Modalità firma batch — {{ corriere }}"
      with back-button, batch toolbar appears, table swaps to 4
      cols + checkbox (☐ / N° ordine / Cliente / N° pezzi / Azioni).
      Footer surfaces "Genera Borderò" + "Seleziona tutti".
-->

<div class="sign-page">

  <!-- ─── Sticky header (mode-aware) ───────────────────────────── -->
  <header class="sign-header">
    @if (chosenCarrier === null) {
      <!-- LIST MODE header -->
      <div class="sign-header__titles">
        <h1 class="sign-header__title">✍️ Ordini da firmare</h1>
        <p class="sign-header__subtitle">
          @if (orders.length > 0) {
            <strong>{{ orders.length }}</strong>
            {{ orders.length === 1 ? 'ordine in attesa di firma' : 'ordini in attesa di firma' }}
            @if (filterTerm) { · filtro <strong>"{{ filterTerm }}"</strong> }
          } @else if (loading) {
            Caricamento ordini…
          } @else {
            Nessun ordine in attesa di firma
          }
        </p>
      </div>

      <button
        type="button"
        class="refresh-btn"
        (click)="getOpenOrders()"
        title="Aggiorna lista"
        aria-label="Aggiorna lista"
      >🔁</button>
    } @else {
      <!-- SIGN-BATCH MODE header -->
      <div class="sign-header__titles">
        <button
          type="button"
          class="back-btn"
          (click)="backToList()"
          title="Torna alla lista completa"
        >← Torna alla lista</button>

        <h1 class="sign-header__title sign-header__title--batch">
          ✍️ Modalità firma batch
          <span class="sign-header__carrier-tag">{{ chosenCarrier }}</span>
        </h1>
        <p class="sign-header__subtitle">
          {{ filteredOrders.length }}
          {{ filteredOrders.length === 1 ? 'ordine' : 'ordini' }} per questo corriere
          @if (selectedCount > 0) {
            · <strong>{{ selectedCount }} selezionati</strong>
          }
        </p>
      </div>
    }
  </header>

  <!-- ─── Filter bar ───────────────────────────────────────────── -->
  <section class="sign-filters" aria-label="Filtri ricerca">

    <!-- Search -->
    <div class="filter-field">
      <label class="filter-field__label" for="searchSign">
        @if (chosenCarrier === null) { Cerca ordine } @else { Cerca nella lista corriere }
      </label>
      <div class="filter-field__input-wrap">
        <input
          id="searchSign"
          type="search"
          class="filter-field__input"
          placeholder="N° ordine, cliente o operatore…"
          [(ngModel)]="filterTerm"
          (ngModelChange)="handleFilterChange()"
          (keyup.enter)="
            chosenCarrier === null
              ? searchOrders(filterTerm)
              : searchAfterCarrier()
          "
        />
        @if (filterTerm) {
          <button
            type="button"
            class="filter-field__clear"
            (click)="clearSearch()"
            aria-label="Rimuovi filtro"
          >×</button>
        }
      </div>
    </div>

    <!-- Date range -->
    <div class="filter-field filter-field--dates">
      <label class="filter-field__label">Data ordine</label>
      <div class="filter-field__date-pair">
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [ngModel]="filters.startingDate | date:'yyyy-MM-dd'"
          (ngModelChange)="onStartDateChange($event)"
          aria-label="Data da"
        />
        <span class="filter-field__date-sep">→</span>
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [ngModel]="filters.endDate | date:'yyyy-MM-dd'"
          (ngModelChange)="onEndDateChange($event)"
          aria-label="Data a"
        />
      </div>
    </div>

    <!-- Carrier dropdown -->
    <div class="filter-field">
      <label class="filter-field__label" for="filterCarrier">Corriere</label>
      <select
        id="filterCarrier"
        class="filter-field__input filter-field__input--select"
        [(ngModel)]="chosenCarrier"
        (ngModelChange)="changeCarier($event)"
      >
        @for (carrier of carriers; track carrier) {
          <option [ngValue]="carrier === showAll ? null : carrier">
            {{ carrier || '— non specificato —' }}
          </option>
        }
      </select>
    </div>

    @if (chosenCarrier === null) {
      <button
        type="button"
        class="btn btn--primary"
        (click)="searchOrders(filterTerm)"
        [disabled]="loading"
      >🔍 Cerca</button>
    } @else {
      <button
        type="button"
        class="btn btn--primary"
        (click)="searchAfterCarrier()"
      >🔍 Cerca</button>
    }
  </section>

  <!-- ─── Batch toolbar (only in sign-batch mode) ──────────────── -->
  @if (chosenCarrier !== null && filteredOrders) {
    <section class="batch-toolbar" aria-label="Azioni batch">
      <button
        type="button"
        class="btn btn--ghost btn--sm"
        (click)="selectAllOrdersPerCarr()"
      >
        @if (areSelected) { ✖ Deseleziona tutti }
        @else { ✓ Seleziona tutti }
      </button>

      <div class="batch-toolbar__spacer"></div>

      @if (selectedCount > 0) {
        <span class="batch-toolbar__count">
          <strong>{{ selectedCount }}</strong>
          {{ selectedCount === 1 ? 'ordine selezionato' : 'ordini selezionati' }}
        </span>
      }

      <button
        type="button"
        class="btn btn--success"
        (click)="openBorderoModal()"
        [disabled]="!isOneSelected"
      >📑 Genera Borderò</button>
    </section>
  }

  <!-- ─── Table card ───────────────────────────────────────────── -->
  <section class="sign-table-card" aria-label="Elenco ordini">

    @if (loading) {
      <div class="table-skeleton" aria-busy="true">
        @for (i of [1, 2, 3, 4, 5, 6, 7, 8]; track i) {
          <div class="table-skeleton__row">
            <div class="table-skeleton__line table-skeleton__line--lg"></div>
            <div class="table-skeleton__line table-skeleton__line--md"></div>
            <div class="table-skeleton__line table-skeleton__line--sm"></div>
          </div>
        }
      </div>
    }

    <!-- ─── LIST MODE table ─── -->
    @else if (chosenCarrier === null) {
      @if (orders.length === 0) {
        <div class="sign-empty">
          <div class="sign-empty__icon" aria-hidden="true">
            @if (filterTerm) { 🔎 } @else { 📭 }
          </div>
          <p class="sign-empty__msg">
            @if (filterTerm) {
              Nessun ordine corrisponde a <strong>"{{ filterTerm }}"</strong>.
            } @else {
              Nessun ordine in attesa di firma al momento.
            }
          </p>
        </div>
      } @else {
        <div class="sign-table-scroll">
          <table class="sign-table" role="table">
            <thead>
              <tr>
                <th class="col-order is-sortable" (click)="sortBy('or_order_number')">
                  N° ordine
                  @if (sortColumn === 'or_order_number') {
                    <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                  }
                </th>
                <th class="col-operator is-sortable" (click)="sortBy('ac_first_name')">
                  Operatore
                  @if (sortColumn === 'ac_first_name') {
                    <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                  }
                </th>
                <th class="col-customer is-sortable" (click)="sortBy('or_carrier_desc')">
                  Corriere
                  @if (sortColumn === 'or_carrier_desc') {
                    <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                  }
                </th>
                <th class="col-customer is-sortable" (click)="sortBy('or_customer')">
                  Cliente
                  @if (sortColumn === 'or_customer') {
                    <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                  }
                </th>
                <th class="col-date is-sortable" (click)="sortBy('or_boxes')">
                  N° Colli
                  @if (sortColumn === 'or_boxes') {
                    <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                  }
                </th>
                <th class="col-date is-sortable" (click)="sortBy('or_order_date')">
                  Data ordine
                  @if (sortColumn === 'or_order_date') {
                    <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                  }
                </th>
                <th class="col-date is-sortable" (click)="sortBy('or_closed_date')">
                  Data evasione
                  @if (sortColumn === 'or_closed_date') {
                    <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                  }
                </th>
                <th class="col-action-complete">Completa</th>
                <th class="col-actions">Azioni</th>
              </tr>
            </thead>
            <tbody>
              @for (
                item of filteredOrders
                  | byCustomerName: ''
                  | byToSignDate: [filters.startingDate, filters.endDate, filters.dateOrginizedBy.value];
                track item.order_id + '-' + ($index)
              ) {
                <tr
                  class="sign-row"
                  (click)="gotoDetails(item.order_id)"
                  title="Apri dettagli ordine"
                >
                  <td class="col-order">
                    <span class="cell-order-num">{{ item.or_order_number || '—' }}</span>
                  </td>
                  <td class="col-operator">
                    {{ item.ac_first_name }} {{ item.ac_last_name }}
                  </td>
                  <td class="col-carrier">{{ item.or_carrier_desc || item.or_carrier_other || '—' }}</td>
                  <td class="col-customer">{{ item.or_customer || '—' }}</td>
                  <td class="col-num">
                    <span class="cell-num">{{ item.or_boxes ?? 0 }}</span>
                  </td>
                  <td class="col-date">
                    <span class="cell-date">
                      {{ item.or_order_date ? (item.or_order_date | date:'dd/MM/yyyy') : '—' }}
                    </span>
                  </td>
                  <td class="col-date">
                    <span class="cell-date">
                      {{ item.or_assigned_date ? (item.or_assigned_date | date:'dd/MM/yyyy') : '—' }}
                    </span>
                  </td>
                  <td class="col-action-complete" (click)="$event.stopPropagation()">
                    <button
                      type="button"
                      class="btn btn--success btn--sm"
                      (click)="openConfirmModal(item.order_id)"
                      title="Completa ordine"
                    >✓ Completa</button>
                  </td>
                  <td class="col-actions" (click)="$event.stopPropagation()">
                    <button
                      type="button"
                      class="icon-btn"
                      title="Apri dettagli"
                      (click)="gotoDetails(item.order_id)"
                    >ℹ️</button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    }

    <!-- ─── SIGN-BATCH MODE table ─── -->
    @else {
      @if (selectedOrders.length === 0) {
        <div class="sign-empty">
          <div class="sign-empty__icon" aria-hidden="true">📭</div>
          <p class="sign-empty__msg">
            Nessun ordine in attesa per <strong>{{ chosenCarrier }}</strong>.
          </p>
        </div>
      } @else {
        <div class="sign-table-scroll">
          <table class="sign-table" role="table">
            <thead>
              <tr>
                <th class="col-check" aria-label="Selezione"></th>
                <th class="col-order">N° Ordine</th>
                <th class="col-customer">Cliente</th>
                <th class="col-num">N° Pezzi</th>
                <th class="col-actions">Azioni</th>
              </tr>
            </thead>
            <tbody>
              @for (
                item of filteredOrders;
                track item.order_id + '-' + ($index)
              ) {
                <tr
                  class="sign-row sign-row--selectable"
                  [class.sign-row--checked]="item.op_checked"
                  (click)="chooseItem(item.indexNo, $event)"
                  title="Seleziona / deseleziona"
                >
                  <td class="col-check">
                    <label class="checkbox-cell" (click)="$event.stopPropagation()">
                      <input
                        type="checkbox"
                        [checked]="item.op_checked"
                        (change)="chooseItem(item.indexNo, $event)"
                      />
                    </label>
                  </td>
                  <td class="col-order">
                    <span class="cell-order-num">{{ item.or_order_number || '—' }}</span>
                  </td>
                  <td class="col-customer">{{ item.or_customer || '—' }}</td>
                  <td class="col-num">
                    <span class="cell-num">{{ item.or_boxes ?? 0 }}</span>
                  </td>
                  <td class="col-actions" (click)="$event.stopPropagation()">
                    <button
                      type="button"
                      class="icon-btn"
                      title="Apri dettagli"
                      (click)="gotoDetails(item.order_id)"
                    >ℹ️</button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    }
  </section>

</div>

<!-- ─── Sub-modali (preserved as-is, restyled in D.2/D.3/D.4) ── -->

@if (openModal.isDaFirmare) {
  <app-ordini-da-firmare-detaggli
    [openedFIRMARE]="openModal.isDaFirmare"
    [order_id]="clickedId"
  ></app-ordini-da-firmare-detaggli>
}

@if (openModal.isConfirmComplete) {
  <app-confirm-complete
    [modalOpened]="openModal.isConfirmComplete"
    [order_id]="clickedIdComplete"
    (modalClosed)="completeOrder(clickedIdComplete)"
  ></app-confirm-complete>
}

@if (openModal.isBordero) {
  <app-bordero-page
    [openedBorder]="openModal.isBordero"
    (answerSubmitted)="sign($event)"
  ></app-bordero-page>
}
